import { format, parseISO } from "date-fns";
import { useTranslation } from "react-i18next";
import { Avatar, Box, Divider, Typography } from "@mui/material";

import { User } from "../../../../types/types";

import { VerificationIcon } from "../../../User/assets/icons/user-info-icons";
import { useUserDataFormatter } from "../../../User/hooks/useUserDataFormatter";

const CandidateInfo = ({
  user,
  jobName,
  overallFitScore,
  createdAt,
}: {
  user: User;
  jobName?: string;
  overallFitScore?: number;
  createdAt?: string;
}) => {
  const { t } = useTranslation("goHire", { keyPrefix: "candidateReportPage" });

  const { fullName, firstTwoChr, role, countryName, media, club } = useUserDataFormatter(user);

  const formattedDate = createdAt ? format(parseISO(createdAt), "yyyy-MM-dd") : "--";

  return (
    <Box sx={styles.candidateDetailsContainer}>
      <Box sx={styles.candidateInfoWrapper}>
        <Box sx={styles.avatarWrapper}>
          <Avatar src={media} sx={styles.avatar}>
            {firstTwoChr}
          </Avatar>
          <VerificationIcon sx={styles.verificationIcon} />
        </Box>
        <Box sx={styles.infoWrapper}>
          <Typography sx={styles.candidateName}>{fullName}</Typography>
          <Typography sx={styles.candidateInfo}>
            {role} {countryName && `• ${countryName}`} {club && `• ${club}`}
          </Typography>
        </Box>
      </Box>
      <Divider />
      <Box sx={styles.dataContainer}>
        <Box component="fieldset" sx={styles.fieldContainer}>
          <Typography component="label" sx={styles.fieldLabel}>
            {t("jobName")}
          </Typography>
          <Typography sx={styles.jobName}>{jobName || "--"}</Typography>
        </Box>
        <Box component="fieldset" sx={styles.fieldContainer}>
          <Typography component="label" sx={styles.fieldLabel}>
            {t("fitScore")}
          </Typography>
          <Typography sx={styles.jobName}>{overallFitScore ?? "--"}%</Typography>
        </Box>
        <Box component="fieldset" sx={styles.fieldContainer}>
          <Typography component="label" sx={styles.fieldLabel}>
            {t("reportDate")}
          </Typography>
          <Typography sx={styles.jobName}>{formattedDate}</Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default CandidateInfo;

const styles = {
  candidateInfoWrapper: {
    display: "flex",
    gap: "24px",
    mb: "12px",
  },
  avatarWrapper: {
    position: "relative",
  },
  avatar: {
    width: "64px",
    height: "64px",
  },
  verificationIcon: {
    position: "absolute",
    bottom: "-12.5px",
    left: "50%",
    transform: "translateX(-50%)",
  },
  infoWrapper: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },
  candidateName: {
    fontFamily: "Montserrat",
    color: "var(--Gray-900, #101828)",
    fontSize: "24px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "32px",
  },
  candidateInfo: {
    color: "var(--Gray-600, #475467)",
    fontFamily: "Montserrat",
    fontSize: "16px",
    fontStyle: "normal",
    fontWeight: 400,
    lineHeight: "24px",
  },
  candidateDetailsContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    borderRadius: "12px",
    border: "1px solid #EAECF0",
    background: "var(--White, #FFF)",
    padding: "24px",
  },
  dataContainer: {
    display: "flex",
    justifyContent: "space-between",
    flexDirection: { xs: "column", sm: "row" },
    gap: "12px",
  },
  fieldContainer: {
    border: "none",
    width: "fit-content",
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    p: 0,
  },
  fieldLabel: {
    color: "var(--Gray-600, #475467)",
    fontFamily: "Montserrat",
    fontSize: "12px",
    fontStyle: "normal",
    fontWeight: 400,
    lineHeight: "18px",
  },
  jobName: {
    fontFamily: "Montserrat",
    color: "var(--black, #141414)",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "20px",
  },
};
